import React, { useState } from 'react';
import { toast } from 'sonner';
import { Mail, Phone, Send, Loader2 } from 'lucide-react';
import { useSubmitContact } from '../data/hooks';
import { track, EVENTS } from '../lib/analytics';
import SEO from '../components/SEO';

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', company: '', topic: 'general', message: '' });
  const { mutate: submitContact, loading: submitting, error: submitError } = useSubmitContact();

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill name, email, and message.');
      return;
    }
    try {
      await submitContact({
        name: form.name,
        email: form.email,
        company: form.company,
        topic: form.topic,
        message: form.message,
      });
      track(EVENTS.CONTACT_SUBMITTED, { topic: form.topic });
      toast.success("Message received — we'll respond within 24 hours.");
      setForm({ name: '', email: '', company: '', topic: 'general', message: '' });
    } catch {
      toast.error('Could not send your message. Please try again.');
    }
  };

  const ic = "w-full border border-black bg-white px-3 py-3 text-sm focus:outline-none";

  return (
    <div data-testid="contact-page" className="surface-obsidian text-platinum">
      <SEO
        title="Contact GIWAHS — Whaitiri Black Limited"
        description="Reach the GIWAHS team at Whaitiri Black Limited. Support, founder enquiries, and supplier onboarding — response within 24 hours during NZ business days."
      />
      <section className="border-b border-black bg-[#0A0A0A] text-white">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-24">
          <span className="label-overline text-neutral-400">/ Contact</span>
          <h1 className="mt-4 font-display tracking-tighter" style={{ fontWeight: 700, fontSize: 'clamp(2.5rem, 6vw, 5rem)', lineHeight: 0.95 }}>
            Let&apos;s talk sourcing.
          </h1>
          <p className="mt-5 max-w-xl text-neutral-300">
            For support, founder programme enquiries, supplier onboarding, partnerships, or press — drop us a line. We respond within <strong className="text-white">24 hours during NZ business days</strong> (Mon–Fri, GMT+12/+13).
          </p>
          <div className="mt-6 inline-flex items-center gap-3 border border-neutral-700 px-4 py-2 font-mono-data text-[11px] uppercase tracking-[0.16em] text-neutral-300">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
            Whaitiri Black Limited · trading as GIWAHS · NZ Co № 6860633
          </div>
        </div>
      </section>

      <section className="surface-obsidian text-platinum">
        <div className="mx-auto grid max-w-[1440px] gap-12 px-5 py-16 md:grid-cols-12 md:px-8 md:py-24">
          <div className="md:col-span-5">
            <span className="label-overline">Reach us</span>
            <div className="mt-6 space-y-5">
              <div className="flex items-start gap-4">
                <Mail size={18} strokeWidth={1.5} className="mt-0.5" />
                <div>
                  <a href="mailto:support@giwahs.com" data-testid="contact-email-support" className="font-display text-lg hover:underline" style={{ fontWeight: 600 }}>support@giwahs.com</a>
                  <div className="font-mono-data text-xs text-neutral-500">General support · 24-hour response, NZ business days</div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Mail size={18} strokeWidth={1.5} className="mt-0.5" />
                <div>
                  <a href="mailto:founders@giwahs.com" data-testid="contact-email-founders" className="font-display text-lg hover:underline" style={{ fontWeight: 600 }}>founders@giwahs.com</a>
                  <div className="font-mono-data text-xs text-neutral-500">Founding Partner programme · Jay Elkington reads every reply</div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Phone size={18} strokeWidth={1.5} className="mt-0.5" />
                <div>
                  <a href="tel:+64272469998" data-testid="contact-phone" className="font-display text-lg hover:underline" style={{ fontWeight: 600 }}>+64 27 246 9998</a>
                  <div className="font-mono-data text-xs text-neutral-500">NZ mobile · Mon–Fri 09:00–17:00 GMT+12/+13</div>
                </div>
              </div>
            </div>

            {/* Founder enquiries CTA */}
            <div className="mt-10 border border-[#DAA520] bg-[#0A0A0A] p-6 text-white" data-testid="founder-enquiries-cta">
              <div className="label-overline text-[#DAA520]">Founder enquiries</div>
              <p className="mt-3 text-sm leading-relaxed text-neutral-300">
                Considering the GIWAHS Founding Partner programme? Worldwide-limited Founding Partnerships, 10-year price-lock, three tiers (Gold · Elite · Enterprise). Jay reads every reply on this thread personally.
              </p>
              <a
                href="mailto:founders@giwahs.com?subject=Founding%20Partner%20enquiry"
                data-testid="founder-enquiries-cta-link"
                className="mt-4 inline-flex items-center gap-2 border border-[#DAA520] bg-[#DAA520] px-4 py-2 text-xs font-mono uppercase tracking-[0.16em] text-[#0A0A0A] hover:bg-white transition-colors"
              >
                Email founders@giwahs.com →
              </a>
            </div>

            <div className="mt-6 border border-black p-6">
              <div className="label-overline">For factories</div>
              <p className="mt-3 text-sm leading-relaxed text-neutral-700">
                Are you a manufacturer? Apply to be listed in the GIWAHS directory. Our team reviews submissions weekly.
              </p>
              <a href="/for-suppliers" data-testid="contact-supplier-apply-link" className="mt-4 inline-block label-overline border-b border-black pb-0.5">Apply as a supplier →</a>
            </div>
          </div>

          <form onSubmit={submit} data-testid="contact-form" className="md:col-span-7 border border-black p-6 md:p-10">
            <span className="label-overline">Send us a message</span>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <div>
                <label className="label-overline">Name</label>
                <input data-testid="contact-page-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={`${ic} mt-2`} />
              </div>
              <div>
                <label className="label-overline">Email</label>
                <input data-testid="contact-page-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={`${ic} mt-2`} />
              </div>
              <div>
                <label className="label-overline">Company</label>
                <input data-testid="contact-page-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className={`${ic} mt-2`} />
              </div>
              <div>
                <label className="label-overline">Topic</label>
                <select data-testid="contact-page-topic" value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} className={`${ic} mt-2`}>
                  <option value="general">General Enquiry</option>
                  <option value="partnership">Partnership</option>
                  <option value="press">Press</option>
                  <option value="supplier">Become a supplier</option>
                </select>
              </div>
            </div>
            <div className="mt-4">
              <label className="label-overline">Message</label>
              <textarea data-testid="contact-page-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={7} className={`${ic} mt-2`} />
            </div>
            <button
              type="submit"
              data-testid="contact-page-submit"
              disabled={submitting}
              className="btn-giwahs btn-giwahs-primary mt-6 inline-flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? <><Loader2 size={14} strokeWidth={1.5} className="animate-spin" /> Sending…</> : <>Send message <Send size={14} strokeWidth={1.5} /></>}
            </button>
            {submitError && (
              <p className="mt-2 text-xs text-red-700" data-testid="contact-error">Failed to send. Please try again.</p>
            )}
          </form>
        </div>
      </section>
    </div>
  );
};

export default Contact;
